import { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../i18n'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../stores'
import { generateAgreementPDF, CURRENT_AGREEMENT_VERSION, CURRENT_HARDCODED_TEMPLATE, isPlatformAgreement } from '../utils/agreementTemplate'
import { agreementTemplateAPI } from '../services/api'
import formatAddress from '../utils/formatAddress'
import { formatSex } from '../utils/formatSex'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * AgreementPreviewModal v194.1
 * 
 * Full-screen modal overlay that renders the Orella Platform Agreement (v4, s101)
 * as an iframe. Used for pre-sign preview (unsigned, read-only).
 */
export default function AgreementPreviewModal({ isOpen, onClose, signatureData: sigProp = '', signedDate: signedProp = '', doctor: doctorProp = null }) {
  const { t } = useI18n()
  const { currentCase } = useCaseStore()
  const { profile } = useProfileStore()
  const [iframeHeight, setIframeHeight] = useState(2400)
  // ★ s101: the preview is the Orella Platform Agreement, exactly what a new signature records
  //   (ShareScreen buildPatientAgreement): the admin panel's active GLOBAL template (no case/doctor
  //   scope) when it is the Platform Agreement, else the default text.
  const [platformTemplate, setPlatformTemplate] = useState(null)
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    agreementTemplateAPI.active()
      .then((tpl) => { if (!cancelled && isPlatformAgreement(tpl) && Array.isArray(tpl.sections) && tpl.sections.length > 0) setPlatformTemplate(tpl) })
      .catch((err) => console.warn('[AgreementPreview] platform agreement fetch failed:', err))
    return () => { cancelled = true }
  }, [isOpen])

  const formatDate = (iso) => {
    if (!iso) return 'N/A'
    try { return new Date(iso).toISOString().split('T')[0] } catch { return iso }
  }

  const agreementHTML = useMemo(() => {
    if (!isOpen || !currentCase) return ''

    const patientName = `${profile?.firstName || profile?.identification?.firstName || ''} ${profile?.lastName || profile?.identification?.lastName || ''}`.trim() || 'Patient'
    const patientDOB = formatDate(profile?.identification?.dob || profile?.dob || profile?.identification?.dateOfBirth)
    const patientSex = formatSex(profile?.gender || profile?.sex || profile?.identification?.sex) || 'N/A'
    const patientOrlId = profile?.id?.slice(-8) || currentCase.id?.slice(-8) || 'N/A'

    // v225.12.0 (#8): Reflect a completed signature when one exists. Priority:
    // explicit prop (in-flight share-sequence signature, before the case is sent)
    // → saved agreement on the case (post-send). The preview previously always
    // passed empty strings, so it read "Signature pending / Signed: N/A" even
    // right after the patient signed.
    const pa = currentCase.patientAgreement || {}
    const resolvedSignature = sigProp || pa.signature || ''
    const resolvedSignedDate = resolvedSignature
      ? (signedProp || (pa.signedAt ? formatDate(pa.signedAt) : formatDate(new Date().toISOString())))
      : ''

    // v226.9.8 (PAC share-sequence fix): in the share flow the case's stored doctor
    // can still be a stale default/placeholder (e.g. the practice owner), so the
    // preview rendered "Dr. Rami" instead of the doctor the patient is actually
    // sending to. When the caller passes the resolved share-target doctor, build the
    // practice block from THAT — so the agreement the patient reads here matches the
    // doctor it is shared with and the eventual signed record. No prop → unchanged
    // behaviour (post-send views + MD onboarding keep using currentCase.doctor).
    const dShare = doctorProp || null
    const d = dShare || currentCase.doctor || {}
    const doctorName = dShare ? '' : (currentCase.doctorName || currentCase.doctor_name || '')
    const parts = doctorName.replace(/^Dr\.?\s*/i, '').split(' ')
    const fullAddress = [
      formatAddress(d.practice_address),
      [d.practice_city, d.practice_state].filter(Boolean).join(', '),
      d.practice_zip
    ].filter(Boolean).join(', ')
    const doctor = {
      firstName: d.first_name || parts[0] || '',
      lastName: d.last_name || parts.slice(1).join(' ') || '',
      credentials: d.credentials || 'MD',
      practiceName: dShare
        ? (d.practice_name || '')
        : (currentCase.practiceName || currentCase.practice_name || d.practice_name || ''),
      practiceAddress: fullAddress,
      practicePhone: d.practice_phone || d.phone || '',
      practiceFax: d.practice_fax || '',
      practiceEmail: d.practice_email || '',
      practiceState: d.practice_state || '',
      practiceCity: d.practice_city || '',
      // TF b34 #8: the practice's own cash prices. Absent from the API payload
      // today, in which case generateAgreementV2 falls back to $99/$249 exactly
      // as before — so this line changes nothing until an MD enters a number.
      // ★ v242.151.0 AGREEMENT-FEE-FIELD-1 — the names the MD side actually
      //   writes. `virtual_consult_fee` / `in_office_consult_fee` exist nowhere
      //   in the API or the panel, so this always fell through to the template's
      //   $99/$249 — which happen to be the same numbers as the stored rates,
      //   which is why the document looked correct while reading nothing.
      virtualFee: d.self_pay_virtual_rate != null ? d.self_pay_virtual_rate : d.selfPayVirtualRate,
      inOfficeFee: d.self_pay_in_office_rate != null ? d.self_pay_in_office_rate : d.selfPayInOfficeRate,
      // v242.239.0 (TEST 2.21): only a v2 render prints these fees; v3 carries no pricing.
    }

    try {
      return generateAgreementPDF({
        patientName,
        patientDOB,
        patientSex,
        patientId: patientOrlId,
        caseId: currentCase.id?.slice(-12) || 'N/A',
        signatureData: resolvedSignature,
        signedDate: resolvedSignedDate,
        doctor,
        agreementVersion: platformTemplate?.version || CURRENT_AGREEMENT_VERSION,
        // P2 repair 2 (NIT-1): without a sections snapshot a new signature is of the hardcoded V3
        //   text (ShareScreen buildPatientAgreement records the same marker); sections still win.
        hardcodedTemplate: platformTemplate ? undefined : CURRENT_HARDCODED_TEMPLATE,
        title: platformTemplate?.title,
        sections: platformTemplate?.sections,
        effectiveDate: platformTemplate?.effectiveDate,
      })
    } catch (err) {
      console.error('[AgreementPreview] Error:', err)
      return ''
    }
  }, [isOpen, currentCase, profile, platformTemplate, sigProp, signedProp, doctorProp])

  // Resize iframe to fit
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'agreementHeight' && e.data.height) {
        setIframeHeight(e.data.height + 40)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const htmlWithReporter = useMemo(() => {
    if (!agreementHTML) return ''
    const script = `<script>
      window.addEventListener('load', function() {
        parent.postMessage({ type: 'agreementHeight', height: document.body.scrollHeight }, '*');
      });
    <\/script>`
    return agreementHTML.replace('</body>', script + '</body>')
  }, [agreementHTML])

  if (!isOpen) return null

  return createPortal(
    <div 
      className="fixed inset-0 z-[200] flex flex-col bg-black/50"
      onClick={onClose}
    >
      <div 
        className="flex-1 flex flex-col bg-white rounded-t-[20px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-safe-top="1"
        /* v242.87.0 — was `mt-12`, a hardcoded 48px gap standing in for the
           status bar. On a Dynamic Island device the top inset is 59pt, so 48px
           left the "Patient Care Agreement" header partly under the clock —
           the same defect TF b35 #28 reported on the Symptom Check-In header.
           A fixed pixel guess cannot track a device it was not measured on. */
        style={{ marginTop: 'calc(env(safe-area-inset-top, 0px) + 12px)', maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 12px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-[15px] font-bold text-[#15354F]">{t('share.patientCareAgreement', 'Patient Care Agreement')}</h2>
          <button onClick={onClose} className="p-1.5 -mr-1.5 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {htmlWithReporter ? (
            <iframe
              srcDoc={htmlWithReporter}
              title="Patient Care Agreement Preview"
              className="w-full border-0"
              style={{ height: iframeHeight, minHeight: 800, background: '#fff' }}
              sandbox="allow-scripts"
            />
          ) : (
            <div className="flex items-center justify-center py-20">
              <OrellaSpinner size={24} />
              <span className="ml-2 text-gray-400 text-sm">{t('common.loading', 'Loading...')}</span>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-full border-[1.5px] border-[#D8E6F0] bg-white text-[#15354F] text-[14px] font-semibold"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
